import { computed, nextTick, onScopeDispose, ref } from 'vue';
import { useAlert } from 'dashboard/composables';

const SOFT_LIMIT = 10;

export const useCustomToolsCatalog = ({
  store,
  t,
  assistantId,
  hasAssistantContext,
  canManageAssistant,
  hasAdvancedOperations,
  hasAdvancedManagement,
  hasCustomToolsEntitlement,
  linkedCustomScenarioCount = () => 0,
  refreshManagement = async () => {},
  notify = useAlert,
}) => {
  const customToolsError = ref('');
  const customToolAvailabilityPending = ref(null);
  const savingCustomToolId = ref(null);
  const createDialogRef = ref(null);
  const deleteDialogRef = ref(null);
  const selectedTool = ref(null);
  const dialogType = ref('');
  let active = true;
  let requestId = 0;

  const customTools = computed(
    () => store.getters['captainCustomTools/getRecords'] || []
  );
  const customToolsMeta = computed(
    () => store.getters['captainCustomTools/getMeta'] || {}
  );
  const customToolsUiFlags = computed(
    () => store.getters['captainCustomTools/getUIFlags'] || {}
  );
  const isFetchingCustomTools = computed(() =>
    Boolean(customToolsUiFlags.value.fetchingList)
  );
  const isCustomToolsPageFetching = computed(
    () => !customToolsError.value && isFetchingCustomTools.value
  );
  const showSoftLimitWarning = computed(
    () =>
      !hasAdvancedManagement.value &&
      Number(customToolsMeta.value.totalCount) > SOFT_LIMIT
  );

  const fetchCustomTools = async (page = 1) => {
    requestId += 1;
    const currentRequestId = requestId;

    try {
      const records = await store.dispatch('captainCustomTools/get', { page });
      if (active && currentRequestId === requestId) {
        customToolsError.value = '';
      }
      return records;
    } catch (error) {
      if (!active || currentRequestId !== requestId) return null;

      customToolsError.value =
        error?.response?.data?.message ||
        error?.message ||
        t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.CUSTOM_LOAD_ERROR');
      return null;
    }
  };

  const initializeCustomTools = () => {
    if (hasCustomToolsEntitlement.value) return fetchCustomTools();
    return null;
  };

  const onPageChange = page => fetchCustomTools(page);

  const openCreateDialog = () => {
    if (!canManageAssistant.value || !hasCustomToolsEntitlement.value) return;
    dialogType.value = 'create';
    selectedTool.value = null;
    nextTick(() => createDialogRef.value?.dialogRef?.open());
  };

  const handleEdit = tool => {
    if (!canManageAssistant.value || !tool) return;
    dialogType.value = 'edit';
    selectedTool.value = tool;
    nextTick(() => createDialogRef.value?.dialogRef?.open());
  };

  const handleDelete = tool => {
    if (!canManageAssistant.value || !tool) return;
    selectedTool.value = tool;
    nextTick(() => deleteDialogRef.value?.dialogRef?.open());
  };

  const handleDialogClose = () => {
    dialogType.value = '';
    selectedTool.value = null;
  };

  const onDeleteSuccess = () => {
    selectedTool.value = null;
    const currentPage = Number(customToolsMeta.value.page) || 1;
    const targetPage =
      customTools.value.length === 1 && currentPage > 1
        ? currentPage - 1
        : currentPage;
    return fetchCustomTools(targetPage);
  };

  const toggleCustomToolAvailability = async tool => {
    if (!canManageAssistant.value || !tool) return;

    const targetAssistantId = assistantId.value;
    savingCustomToolId.value = tool.id;
    try {
      await store.dispatch('captainCustomTools/update', {
        id: tool.id,
        enabled: tool.enabled === false,
      });
      if (
        hasAdvancedOperations.value &&
        hasAssistantContext.value &&
        targetAssistantId === assistantId.value
      ) {
        await refreshManagement(targetAssistantId);
      }
      if (targetAssistantId === assistantId.value) {
        notify(
          t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.CUSTOM_AVAILABILITY_UPDATED')
        );
      }
    } catch (error) {
      if (targetAssistantId === assistantId.value) {
        notify(
          error?.message ||
            t(
              'CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.CUSTOM_AVAILABILITY_UPDATE_ERROR'
            )
        );
      }
    } finally {
      if (savingCustomToolId.value === tool.id) {
        savingCustomToolId.value = null;
      }
    }
  };

  const requestToggleCustomToolAvailability = tool => {
    if (!canManageAssistant.value || !tool) return undefined;
    if (tool.enabled !== false && linkedCustomScenarioCount(tool) > 0) {
      customToolAvailabilityPending.value = tool;
      return undefined;
    }
    toggleCustomToolAvailability(tool);
    return undefined;
  };

  const confirmToggleCustomToolAvailability = async () => {
    const tool = customToolAvailabilityPending.value;
    customToolAvailabilityPending.value = null;
    if (tool) await toggleCustomToolAvailability(tool);
  };

  const handleCardAction = ({ action, id }) => {
    const tool = customTools.value.find(record => record.id === id);
    if (action === 'edit') return handleEdit(tool);
    if (action === 'delete') return handleDelete(tool);
    if (action === 'toggle') return requestToggleCustomToolAvailability(tool);
    return undefined;
  };

  const closeCustomToolAvailabilityDialog = () => {
    customToolAvailabilityPending.value = null;
  };

  onScopeDispose(() => {
    active = false;
    requestId += 1;
  });

  return {
    customTools,
    customToolsMeta,
    customToolsError,
    isFetchingCustomTools,
    isCustomToolsPageFetching,
    showSoftLimitWarning,
    customToolAvailabilityPending,
    savingCustomToolId,
    createDialogRef,
    deleteDialogRef,
    selectedTool,
    dialogType,
    fetchCustomTools,
    initializeCustomTools,
    onPageChange,
    openCreateDialog,
    handleDialogClose,
    onDeleteSuccess,
    handleCardAction,
    confirmToggleCustomToolAvailability,
    closeCustomToolAvailabilityDialog,
  };
};
